import {
	Grid,
	GridColumn as Column,
	GridSelectionChangeEvent,
	GridHeaderSelectionChangeEvent,
	GridColumnResizeEvent,
	GridToolbar,
	GridColumnProps,
	GridRowProps,
	GridItemChangeEvent,
	GridCustomHeaderCellProps,
	GridCustomCellProps,
	GridExpandChangeEvent,
	GridColumnReorderEvent,
} from "@progress/kendo-react-grid";
import "./custom-cells/custom-cells.css";
import {
	cloneElement,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import {
	idGetter,
	processWithSelected,
	processEdit,
	processWithCollapsed,
	getIsGrouped,
	getSelectedRows,
	getClipboardData,
	setTableConfig,
	applyColumnLayout,
} from "./table-helpers";
import {
	getSelectedState,
	setGroupIds,
} from "@progress/kendo-react-data-tools";
import { GridSelectableSettings } from "@progress/kendo-react-grid";
import { TableConfig } from "../../types/types";
import { DATA_ITEM_KEY, SELECTED_FIELD } from "./table-helpers";
import { getTrueKeys } from "../../lib/utils/utils-functions";
import { useClipboard } from "../../context/ClipboardContext";
import { useShortCutListener } from "../../hooks/useShortcutListener";
import { Logger } from "../../../../lib/logger/Logger";
import { process, orderBy } from "@progress/kendo-data-query";
import { Button as MuiButton, Skeleton } from "@mui/material";
import { HeaderCustomCell } from "./custom-cells/header-custom-cell.component";
import { GroupHeaderCustomCell } from "./custom-cells/group-header-custom-cell.component";
import { Translate } from "../translate/translate.component";
import { showNotification } from "../../store/Central/selectors";
import { TableNavigationProvider } from "./table-navigation-context";

const selectable: GridSelectableSettings = {
	enabled: false, // Determines if selection is allowed (only applies to selection via row, not via checkbox column).
	drag: false, // Determines if drag selection is allowed.
	cell: false, // Determines if cell selection is allowed.
	mode: "multiple", // Single or multiple.
};

/**
 * CustomTable - Base table component
 * - Listens to copy and cut events, listens to paste events and checks if the pasted content is valid
 * @param onConfigChange - Sends up new TableConfig object, when columns are reordered or resized
 * @param onRowChange - Sends up edited row object
 * @param onSelectedChange - Sends up array of currently selected rows
 * @param onPasteRows - Sends up array of pasted rows (does not add them to the table by itself)
 * @param dataCompatibility - Array of strings that indicate what rows can be pasted via clipboard
 *
 * To add a "select" column for selecting rows, add a column to the tableConfig with the field "selected".
 */
// True when the pointer is in the lower half of the hovered row, i.e. the dragged row
// should drop after it rather than before it.
const isDropAfter = (event: React.DragEvent): boolean => {
	const rect = event.currentTarget.getBoundingClientRect();
	return event.clientY > rect.top + rect.height / 2;
};

export interface BulkOpsState {
	selectedCount: number;
	handleCopy: () => void;
	handleCut: () => void;
	handlePaste: (forceDuplicate: boolean) => void;
	handleDelete: () => void;
	handleDeselectAll: () => void;
	hasClipboardData: boolean;
}

export const CustomTable: React.FC<{
	rows: any[];
	userConfig?: TableConfig | null;
	defaultConfig: TableConfig;
	toolbarChildren?: React.ReactNode;
	onConfigChange: (newConfig: TableConfig) => void;
	onRowChange: (newRow: any, field?: string) => void;
	onSelectedChange: (selected: any[]) => void;
	onPasteRows: (pastedRows: any[], forceDuplicate?: boolean) => void;
	onCutRows: (cutRows: any[]) => void;
	onDeleteRows: (deletedRows: any[]) => void;
	onUndo?: () => void;
	onRedo?: () => void;
	dataCompatibility?: string[];
	noToolbar?: boolean;
	noGroups?: boolean;
	disabled?: boolean;
	externalLoading?: boolean;
	externalBulkOps?: boolean;
	onBulkOpsStateChange?: (state: BulkOpsState | null) => void;
	/**
	 * Opt-in row reordering. When provided, rows become draggable and a column-header
	 * sort is baked into a persisted manual order (the transient sort is then dropped)
	 * instead of staying a view-only sort. Receives the full id list in the new order.
	 */
	onRowReorder?: (orderedIds: string[]) => void;
}> = ({
	rows: initialRows,
	userConfig,
	defaultConfig,
	toolbarChildren,
	onConfigChange,
	onRowChange,
	onSelectedChange,
	onPasteRows,
	onCutRows,
	onDeleteRows,
	onUndo,
	onRedo,
	dataCompatibility = [],
	noToolbar = false,
	noGroups = false,
	disabled = false,
	externalLoading = false,
	externalBulkOps = false,
	onBulkOpsStateChange,
	onRowReorder,
}) => {
	// Memoizing rows and tableConfig to prevent unnecessary re-rendering
	const rows = useMemo(() => initialRows, [initialRows]);

	const [lastUpdated, setLastUpdated] = useState<{
		time: Date;
		items: { code_e: string; quantity: number }[];
		last: string[];
	}>({ time: new Date(), items: [], last: [] });
	const tableConfig = useMemo(
		() => setTableConfig(defaultConfig, userConfig),
		[defaultConfig, userConfig]
	);

	const visibleTechnicianFields = useMemo(() => {
		const technicianFieldsSet = new Set([
			"profile_id",
			"profile2_id",
			"profile3_id",
		]);
		return tableConfig.columns
			.filter(
				(col: any) =>
					col.show !== false &&
					col.field &&
					technicianFieldsSet.has(col.field)
			)
			.sort((a: any, b: any) => (a.orderIndex ?? 0) - (b.orderIndex ?? 0))
			.map((col: any) => col.field as string);
	}, [tableConfig]);

	const visibleTechnicianGroupFields = useMemo(() => {
		const technicianGroupFieldsSet = new Set([
			"profile_id",
			"techn_perc",
			"profile2_id",
			"techn_perc_2",
			"profile3_id",
			"techn_perc_3",
		]);
		return tableConfig.columns
			.filter(
				(col: any) =>
					col.show !== false &&
					col.field &&
					technicianGroupFieldsSet.has(col.field)
			)
			.sort((a: any, b: any) => (a.orderIndex ?? 0) - (b.orderIndex ?? 0))
			.map((col: any) => col.field as string);
	}, [tableConfig]);

	const allNavigableFields = useMemo(() => {
		const navigableFieldsSet = new Set([
			"code",
			"quantity",
			"unit",
			"profile_id",
			"profile2_id",
			"techn_perc_2",
			"profile3_id",
			"techn_perc_3",
			"description",
			"note",
			"lot",
			"is_req_dr_tax",
			"tp_value",
			"is_warranty",
			"discount",
			"price",
			"additional_text",
			"selected",
			"is_print_additional_text",
		]);
		const result = tableConfig.columns
			.filter(
				(col: any) =>
					col.show !== false &&
					col.field &&
					navigableFieldsSet.has(col.field)
			)
			.sort((a: any, b: any) => (a.orderIndex ?? 0) - (b.orderIndex ?? 0))
			.map((col: any) => col.field as string);
		return result;
	}, [tableConfig]);

	const [data, setData] = useState<any[]>([]);
	const [collapsedState, setCollapsedState] = useState<string[]>([]);
	const [internalLoading, setInternalLoading] = useState<boolean | null>(
		null
	);
	const [selectedState, setSelectedState] = useState<{
		[id: string]: boolean | number[];
	}>({});
	const [isGrouped, setIsGrouped] = useState<boolean>(!noGroups);
	const [isHovering, setIsHovering] = useState<boolean>(false);

	const { clipboardData, setClipboardData } = useClipboard();

	const handleCopy = () => {
		const selectedRows = getSelectedRows(rows, selectedState);
		const newClipboardData = getClipboardData(
			selectedRows,
			dataCompatibility
		);
		if (selectedRows.length === 0) {
			return;
		}
		Logger.log("Copy rows", selectedRows);
		setClipboardData(newClipboardData);
	};

	const handleCut = () => {
		const selectedRows = getSelectedRows(rows, selectedState);
		const newClipboardData = getClipboardData(
			selectedRows,
			dataCompatibility
		);
		if (selectedRows.length === 0) {
			return;
		}
		Logger.log("Cut rows", selectedRows);
		setClipboardData(newClipboardData);
		onCutRows(selectedRows);
		setSelectedState({});
	};

	const handleDelete = () => {
		const selectedRows = getSelectedRows(rows, selectedState);
		if (selectedRows.length === 0) {
			return;
		}
		Logger.log("Delete rows", selectedRows);
		onDeleteRows(selectedRows);
		setSelectedState({});
	};

	const handlePaste = (
		forceDuplicate: boolean = false,
		bypassHoverCheck: boolean = false
	) => {
		if (!bypassHoverCheck && !isHovering) {
			return;
		}
		if (clipboardData.data.length === 0) {
			return;
		}
		Logger.log("Paste rows", clipboardData);
		onPasteRows(clipboardData.data, forceDuplicate);
	};

	const selectedCount = useMemo(
		() => Object.values(selectedState).filter(Boolean).length,
		[selectedState]
	);

	useEffect(() => {
		if (!onBulkOpsStateChange) return;
		if (selectedCount > 0) {
			onBulkOpsStateChange({
				selectedCount,
				handleCopy,
				handleCut,
				handlePaste: (forceDuplicate: boolean) =>
					handlePaste(forceDuplicate, true),
				handleDelete,
				handleDeselectAll: () => {
					setSelectedState({});
					onSelectedChange([]);
				},
				hasClipboardData: clipboardData.data.length > 0,
			});
		} else {
			onBulkOpsStateChange(null);
		}
	}, [selectedCount, clipboardData.data.length, onBulkOpsStateChange]);

	// TODO: also listen to escape events outside of focusing the cell input
	useShortCutListener((key) => {
		switch (key) {
			case "c":
				handleCopy();
				break;
			case "x":
				handleCut();
				break;
			case "v":
				// Keyboard shortcut always merges (doesn't duplicate)
				handlePaste(false);
				break;
			case "Backspace":
				handleDelete();
				break;
			case "z":
				if (onUndo) {
					onUndo();
				}
				break;
			case "redo":
				if (onRedo) {
					onRedo();
				}
				break;
			default:
				break;
		}
	});

	useEffect(() => {
		if ((!data || !data.length) && rows.length) setInternalLoading(true);
		else setInternalLoading(false);
	}, [data]);

	useEffect(() => {
		Logger.log("Starting processing");
		const dataState = tableConfig.dataState ?? {};
		const newData = process(rows, dataState);
		let processedWithCollapsed = [];
		const isGrouped = getIsGrouped(dataState); // do not use isGrouped from state, because it might not be set yet
		setIsGrouped(isGrouped);

		if (isGrouped) {
			// setGroupIds adds a unique id to each group
			setGroupIds({
				data: newData.data,
				group: dataState.group,
			});
			processedWithCollapsed = processWithCollapsed(
				newData.data,
				collapsedState
			);
		} else {
			processedWithCollapsed = newData.data;
		}

		const dataWithSelected = processWithSelected(
			processedWithCollapsed,
			selectedState,
			isGrouped
		);

		Logger.log("Finished processing", dataWithSelected);
		setData(dataWithSelected);
	}, [rows, tableConfig.dataState, collapsedState, selectedState]);

	// Handles the following changes of "dataState": sorting, grouping
	const handleDataStateChange = (event: any) => {
		Logger.log("handleDataStateChange", event);
		const nextState = event.dataState ?? {};

		// With row reordering enabled, a column-header sort also becomes the persisted
		// order, so the print picks it up — for every sort, including one on top of the
		// default `type` grouping (group fields become the leading sort keys). orderBy
		// returns a flat, full-length list, so the persisted order always covers every row.
		// The sort stays active in the view so the sort indicator and asc/desc toggle keep
		// working normally; the baked positions just mirror it. A manual drag later clears
		// the sort to take over (see handleRowDrop).
		if (onRowReorder && nextState.sort?.length) {
			const groupSort = (nextState.group ?? []).map(
				(descriptor: any) => ({
					field: descriptor.field,
					dir: descriptor.dir ?? "asc",
				})
			);
			const orderedIds = orderBy(rows, [
				...groupSort,
				...nextState.sort,
			]).map((item) => idGetter(item));
			onRowReorder(orderedIds);
		}

		const isGrouped = getIsGrouped(nextState);
		Logger.log("isGrouped", isGrouped, nextState);
		setIsGrouped(isGrouped);
		Logger.log("Set isGrouped", isGrouped);
		onConfigChange({
			columns: tableConfig.columns,
			dataState: nextState,
		});
	};

	const handleExpandChange = useCallback(
		(event: GridExpandChangeEvent) => {
			const item = event.dataItem;
			if (item.groupId) {
				const collapsedIds = !event.value
					? [...collapsedState, item.groupId]
					: collapsedState.filter(
							(groupId) => groupId !== item.groupId
						);
				setCollapsedState(collapsedIds);
				setData((prevData) =>
					processWithCollapsed(prevData, collapsedIds)
				);
			}
		},
		[collapsedState]
	);

	const handleColumnResize = (event: GridColumnResizeEvent) => {
		Logger.log("handleColumnResize", event);

		if (event.end) {
			onConfigChange({
				columns: applyColumnLayout(tableConfig.columns, event.columns),
				dataState: tableConfig.dataState,
			});
		}
	};

	const handleColumnReorder = (event: GridColumnReorderEvent) => {
		Logger.log("handleColumnReorder", event);
		onConfigChange({
			columns: applyColumnLayout(tableConfig.columns, event.columns),
			dataState: tableConfig.dataState,
		});
	};

	// TODO check out getSelectedState
	const completeSelectionChange = useCallback(
		(newSelectedState: any, isGrouped: boolean) => {
			setSelectedState(newSelectedState);
			onSelectedChange(getTrueKeys(newSelectedState));
			Logger.log("completeSelectionState", newSelectedState, isGrouped);
			setData((prevData) =>
				processWithSelected(prevData, newSelectedState, isGrouped)
			);
		},
		[onSelectedChange]
	);

	const handleSelectionChange = useCallback(
		(event: GridSelectionChangeEvent) => {
			const newSelectedState: any = getSelectedState({
				event,
				selectedState: selectedState,
				dataItemKey: DATA_ITEM_KEY,
			});
			completeSelectionChange(newSelectedState, isGrouped);
			Logger.log("handleSelectionChange", event);
		},
		[selectedState, isGrouped, completeSelectionChange]
	);

	const handleHeaderSelectionChange = useCallback(
		(event: GridHeaderSelectionChangeEvent) => {
			const checkboxElement: any = event.syntheticEvent.target;
			const checked = checkboxElement.checked;
			const newSelectedState: any = {};

			event.dataItems.forEach((item) => {
				newSelectedState[idGetter(item)] = checked;
			});
			completeSelectionChange(newSelectedState, isGrouped);
			Logger.log("handleHeaderSelectionChange", event);
		},
		[isGrouped, completeSelectionChange]
	);

	const handleItemChange = (event: GridItemChangeEvent) => {
		// Selection toggles must keep working in a disabled (e.g. exported) table so rows
		// can still be selected and copied; only real data edits are blocked.
		if (disabled && event.field !== SELECTED_FIELD) {
			showNotification({
				message: "Tabelle ist deaktiviert",
				type: "error",
			});
			return;
		}

		Logger.log("handleItemChange", event);

		const editedItemID = event.dataItem[DATA_ITEM_KEY];
		if (!event.field) {
			Logger.warn("No field in event", event);
			return;
		}
		const editedField = event.field as string;
		const editedValue = event.value;
		setData((prevData) =>
			processEdit(
				prevData,
				editedItemID,
				editedField,
				editedValue,
				isGrouped
			)
		);

		onRowChange(
			{ ...event.dataItem, [editedField]: editedValue },
			editedField
		);
	};

	const handleMouseEnter = () => {
		setIsHovering(true);
	};

	const handleMouseLeave = () => {
		setIsHovering(false);
	};

	const updateLastUpdatedState = () => {
		const codesToBeUpdated: string[] = [];

		// if new row is added, set the last updated item to the new row by checking which element in rows is not in lastUpdated.items
		if (rows.length > lastUpdated.items.length) {
			const foundRowCode = rows.find(
				(row) =>
					!lastUpdated.items.find(
						(item) => item.code_e === row.code_e
					)
			)?.code_e;
			if (foundRowCode) {
				codesToBeUpdated.push(foundRowCode);
			}
		} else {
			// if no new row is added, check which item has a different quantity than before
			rows.forEach((row) => {
				lastUpdated.items.forEach((item) => {
					if (
						item.code_e === row.code_e &&
						item.quantity !== Number(row.quantity)
					) {
						codesToBeUpdated.push(row.code_e);
					}
				});
			});
		}

		setLastUpdated((prev) => ({
			time: new Date(),
			items: rows.map((row) => {
				return {
					code_e: row.code_e,
					quantity: Number(row.quantity),
				};
			}),
			last: codesToBeUpdated || prev.last,
		}));

		setTimeout(() => {
			setLastUpdated((prev) => {
				return {
					...prev,
					last: [],
				};
			});
		}, 2000);
	};

	useEffect(() => {
		updateLastUpdatedState();
	}, [rows]);

	const draggedRowId = useRef<string | null>(null);
	// Which row the drop line is shown on, and on which edge (before/after the row).
	const [dropTarget, setDropTarget] = useState<{
		id: string;
		after: boolean;
	} | null>(null);

	const handleRowDragStart = useCallback(
		(event: React.DragEvent, rowId: string) => {
			// Never hijack text selection / editing happening inside an editable cell.
			const target = event.target as HTMLElement;
			if (
				target.closest(
					"input, textarea, select, [contenteditable=true]"
				)
			) {
				event.preventDefault();
				return;
			}
			draggedRowId.current = rowId;
			event.dataTransfer.effectAllowed = "move";
		},
		[]
	);

	const handleRowDragOver = useCallback(
		(event: React.DragEvent, rowId: string) => {
			if (!draggedRowId.current) return;
			event.preventDefault();
			const after = isDropAfter(event);
			setDropTarget((prev) =>
				prev && prev.id === rowId && prev.after === after
					? prev
					: { id: rowId, after }
			);
		},
		[]
	);

	const clearDrag = useCallback(() => {
		draggedRowId.current = null;
		setDropTarget(null);
	}, []);

	const handleRowDrop = useCallback(
		(event: React.DragEvent, targetRowId: string) => {
			event.preventDefault();
			const draggedId = draggedRowId.current;
			const after = isDropAfter(event);
			clearDrag();
			if (!onRowReorder || !draggedId || draggedId === targetRowId)
				return;

			// `rows` mirrors the visible order (any active sort is also applied to the
			// store), so index math against it matches what the user sees while dragging.
			const orderedIds = rows.map((row) => idGetter(row) as string);
			const from = orderedIds.indexOf(draggedId);
			if (from === -1) return;
			orderedIds.splice(from, 1);
			let to = orderedIds.indexOf(targetRowId);
			if (to === -1) return;
			if (after) to += 1;
			orderedIds.splice(to, 0, draggedId);
			onRowReorder(orderedIds);

			// A manual drag overrides any active column sort, otherwise the view would
			// immediately re-sort and discard the drop.
			if (tableConfig.dataState?.sort?.length) {
				const { sort: _sort, ...stateWithoutSort } =
					tableConfig.dataState;
				onConfigChange({
					columns: tableConfig.columns,
					dataState: stateWithoutSort,
				});
			}
		},
		[rows, onRowReorder, tableConfig, onConfigChange, clearDrag]
	);

	const rowRender = useCallback(
		(
			trElement: React.ReactElement<HTMLTableRowElement>,
			props: GridRowProps
		) => {
			const lastUpdatedRow = lastUpdated.last.includes(
				props.dataItem.code_e
			);
			const blue = { backgroundColor: "var(--tertiary-200)" };
			const trProps: any = { style: lastUpdatedRow ? blue : {} };

			// Group-header rows have no id — skip them so they stay non-draggable
			// and never match the drop target.
			const rowId = idGetter(props.dataItem) as string | undefined;
			if (onRowReorder && rowId) {
				trProps.draggable = true;
				trProps.onDragStart = (event: React.DragEvent) =>
					handleRowDragStart(event, rowId);
				trProps.onDragOver = (event: React.DragEvent) =>
					handleRowDragOver(event, rowId);
				trProps.onDrop = (event: React.DragEvent) =>
					handleRowDrop(event, rowId);
				trProps.onDragEnd = clearDrag;
				trProps.style = { ...trProps.style, cursor: "grab" };
				// Blue drop line on the target edge, matching the service catalog.
				if (dropTarget && dropTarget.id === rowId) {
					trProps.style = {
						...trProps.style,
						boxShadow: dropTarget.after
							? "inset 0 -2px 0 0 #3b82f6"
							: "inset 0 2px 0 0 #3b82f6",
					};
				}
			}

			return cloneElement(
				trElement,
				{ ...trProps },
				// @ts-expect-error this is how kendo react does it in their guides
				trElement.props.children
			);
		},
		[
			lastUpdated,
			onRowReorder,
			handleRowDragStart,
			handleRowDragOver,
			handleRowDrop,
			clearDrag,
			dropTarget,
		]
	);

	if (internalLoading || externalLoading)
		return (
			<Translate>
				<div>
					<Grid
						data={rows}
						cells={{
							group: {
								groupHeader:
									GroupHeaderCustomCell as React.ComponentType<GridCustomCellProps>,
							},
							headerCell:
								// @ts-expect-error This is the type defined by Kendo but doesn't overlap with the type of the custom cell
								HeaderCustomCell as React.ComponentType<GridCustomHeaderCellProps>,
						}}
					>
						{!noToolbar && (
							<GridToolbar>
								{Object.values(selectedState).filter((e) => e)
									.length > 0 ? (
									<div
										style={{
											display: "flex",
											alignItems: "center",
											gap: "10px",
										}}
									>
										<MuiButton
											disabled
											sx={{
												color: "var(--grey-400)",
												backgroundColor:
													"var(--grey-100)",
											}}
										>
											Kopieren
										</MuiButton>
										<MuiButton disabled>
											Ausschneiden
										</MuiButton>
										{clipboardData.data.length > 0 && (
											<MuiButton disabled>
												Einfügen
											</MuiButton>
										)}
										<MuiButton disabled>Löschen</MuiButton>
									</div>
								) : (
									<>{toolbarChildren && toolbarChildren}</>
								)}
							</GridToolbar>
						)}
						{tableConfig.columns.map((column) => {
							return (
								<Column
									field={column.field}
									key={column.field}
									title={column.title}
									width={column?.width || "100px"}
									cell={() => {
										return (
											<td
												style={{
													padding: "0 4px",
													margin: 0,
												}}
											>
												<Skeleton
													variant="text"
													width={column?.width || 100}
													style={{
														padding: 0,
														margin: 0,
														height: 26,
													}}
												/>
											</td>
										);
									}}
								/>
							);
						})}
					</Grid>
				</div>
			</Translate>
		);
	return (
		<TableNavigationProvider
			value={{
				visibleTechnicianFields,
				visibleTechnicianGroupFields,
				allNavigableFields,
				totalRows: rows.length,
				processedItems: data,
			}}
		>
			<div
				onMouseEnter={handleMouseEnter}
				onMouseLeave={handleMouseLeave}
				// When disabled (e.g. the document is exported) the table is read-only:
				// editable cell inputs are deactivated via CSS, but the selection
				// checkboxes stay live so rows can still be selected and copied.
				className={disabled ? "custom-table-readonly" : undefined}
				style={{
					opacity: disabled ? 0.7 : 1,
				}}
				aria-disabled={disabled || undefined}
			>
				<Translate>
					<Grid
						size="small"
						data={data}
						dataItemKey={DATA_ITEM_KEY}
						reorderable={!disabled}
						sortable={!disabled}
						groupable={!noGroups && !disabled}
						resizable={true}
						onDataStateChange={handleDataStateChange}
						onExpandChange={handleExpandChange}
						expandField="expanded"
						selectable={selectable}
						selectedField={SELECTED_FIELD}
						onSelectionChange={handleSelectionChange}
						onHeaderSelectionChange={handleHeaderSelectionChange}
						onColumnResize={handleColumnResize}
						onColumnReorder={handleColumnReorder}
						onItemChange={handleItemChange}
						onContextMenu={(event) =>
							Logger.log("onContextMenu", event)
						}
						{...tableConfig.dataState}
						cells={{
							group: {
								groupHeader: GroupHeaderCustomCell as any,
							},
							headerCell: HeaderCustomCell as any,
						}}
						rowRender={rowRender}
					>
						{!noToolbar &&
							((!externalBulkOps && selectedCount > 0) ||
								toolbarChildren) && (
								<GridToolbar>
									{!externalBulkOps && selectedCount > 0 ? (
										<div
											style={{
												display: "flex",
												alignItems: "center",
												gap: "10px",
											}}
										>
											<MuiButton
												onClick={() => handleCopy()}
												variant="outlined"
											>
												Kopieren
											</MuiButton>
											<MuiButton
												onClick={() => handleCut()}
												variant="outlined"
											>
												Ausschneiden
											</MuiButton>
											{clipboardData.data.length > 0 && (
												<MuiButton
													onClick={(event) => {
														const metaKeyPressed =
															event.metaKey ||
															event.ctrlKey;
														handlePaste(
															metaKeyPressed,
															true
														);
													}}
													variant="outlined"
												>
													Einfügen
												</MuiButton>
											)}
											<MuiButton
												onClick={() => handleDelete()}
												variant="outlined"
											>
												Löschen
											</MuiButton>
										</div>
									) : (
										<>{toolbarChildren}</>
									)}
								</GridToolbar>
							)}
						{tableConfig.columns.map((column: GridColumnProps) => {
							if (column.field === SELECTED_FIELD) {
								return (
									<Column
										key={column.id}
										field={SELECTED_FIELD}
										width={
											tableConfig.columns.find(
												(col) =>
													col.field === SELECTED_FIELD
											)?.width || "50px"
										}
										// using the default data is sufficient, because it is only needed to have a list of all items and their ids
										// whether all items are selected is determined by selectedState
										headerSelectionValue={
											rows.findIndex(
												(item: any) =>
													!selectedState[
														idGetter(item)
													]
											) === -1
										}
										cell={(props) => {
											if (
												props.rowType != "groupHeader"
											) {
												return (
													<td
														style={{
															padding: 0,
															margin: 0,
														}}
													>
														<div
															style={{
																display: "flex",
																justifyContent:
																	"center",
																alignItems:
																	"center",
																height: "100%",
															}}
														>
															<input
																type="checkbox"
																checked={
																	selectedState[
																		idGetter(
																			props.dataItem
																		)
																	] as boolean
																}
																onChange={(
																	e
																) => {
																	const newSelectedState =
																		{
																			...selectedState,
																			[idGetter(
																				props.dataItem
																			)]:
																				e
																					.target
																					.checked,
																		};
																	completeSelectionChange(
																		newSelectedState,
																		isGrouped
																	);
																}}
																style={{
																	margin: 0,
																	padding: 0,
																}}
															/>
														</div>
													</td>
												);
											} else {
												return <td></td>;
											}
										}}
									/>
								);
							}
							// GridColumnProps has no "show" property, but the kendo react documentation suggests to use it like this
							if ((column as any).show) {
								return (
									<Column
										key={column.field}
										field={column.field}
										title={column.title}
										width={column.width}
										cells={column?.cells || undefined}
										{...column}
									/>
								);
							}
						})}
					</Grid>
				</Translate>
			</div>
		</TableNavigationProvider>
	);
};
